import { Inject, Injectable } from '@angular/core';
import { AuthService, LogoutOptions } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  defaultIfEmpty,
  filter,
  firstValueFrom,
  from,
  interval,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  finalize,
  catchError,
  throwError,
  timeout,
} from 'rxjs';
import jwt_decode from 'jwt-decode';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { SegmentSchema, SegmentService } from '@sondermind/segment';
import { AmplitudeFeatureFlagService } from '@sondermind/amplitude';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';

// Errors that should NOT trigger logout - these are benign errors during initialization or user actions
const AUTH0_SAFE_ERRORS = [
  'Invalid state', // User hit back button, refreshed callback URL, or bookmarked callback
];
// Legacy constants for monitoring/logging
const AUTH0_ACCESS_DENIED_ERRORS = ['access_denied', 'hs_context_invalid'];
const HEADSPACE_CONTEXT_CLAIM = 'https://www.headspace.com/context';
// Paths that should be preserved in appState when redirecting to Auth0 login
const DEEPLINK_REDIRECT_PATHS = ['/therapy-journey', '/messaging'];
interface AppState {
  target: string;
}
interface ITokenInfo {
  sub: string;
  meta: {
    authsvc_account_id: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class Auth0TokenService {
  auth0TokenBehaviorSubject$ = new BehaviorSubject<string>('');
  private auth0EnabledBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private isFirstLoginFromAuth0Signup = new BehaviorSubject<boolean>(false);

  /**
   * Shared observable for token refresh - ensures only one refresh operation happens at a time.
   * When multiple callers need a token simultaneously, they all share the same in-flight request.
   */
  private refreshInProgress$: Observable<string> | null = null;

  heartbeatSubscription: Subscription | null = null;
  auth0Enabled$ = this.auth0EnabledBehaviorSubject$.asObservable();
  auth0Token$ = this.auth0TokenBehaviorSubject$.asObservable();
  authSuccess$ = new BehaviorSubject<boolean>(false);
  isFirstLoginFromAuth0Signup$ =
    this.isFirstLoginFromAuth0Signup.asObservable();
  useAuth0Fix = false;
  constructor(
    private auth0: AuthService,
    private logger: Logger,
    private configService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>,
    private amplitudeFeatureFlagService: AmplitudeFeatureFlagService,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
  ) {
    this.auth0.error$.subscribe((err: any) => {
      const isSafeError = AUTH0_SAFE_ERRORS.includes(err.message);

      this.logger.error(`Auth0 error: ${err.message}`, {
        errorType: err.message,
        isAccessDenied: AUTH0_ACCESS_DENIED_ERRORS.includes(err.message),
        error: err.error
      });
      this.logger.addAction('Auth Refresh Failed', { error: `Auth0 error: ${err.message}`, errorType: err.message, isAccessDenied: AUTH0_ACCESS_DENIED_ERRORS.includes(err.message) });
      // Handle safe errors that don't require logout
      if (isSafeError) {
        this.logger.info(`Safe error detected (${err.message}) - not logging out`);
        this.logger.addAction('Auth Refresh Info', { message: `Safe error detected (${err.message}) - not logging out` });
        // For "Invalid state", redirect to home to clear the callback URL
        // BUT: Don't redirect if we're on the calendar OAuth callback route
        // (Google Calendar OAuth uses 'code' param which Auth0 mistakes for its own callback)
        if (err.message === 'Invalid state') {
          const currentPath = window.location.pathname;
          const isCalendarOAuth = currentPath.includes('calendar_external_authorization');

          if (isCalendarOAuth) {
            this.logger.info('Ignoring Auth0 "Invalid state" error - on calendar OAuth callback route');
            this.logger.addAction('Auth Refresh Info', { message: 'Ignoring Auth0 "Invalid state" error - on calendar OAuth callback route' });
            return;
          }

          this.logger.info('Redirecting to home due to Auth0 "Invalid state" error');
          this.logger.addAction('Auth Refresh Info', { error: 'Redirecting to home due to Auth0 "Invalid state" error' });
          window.location.href = '/';
        }
        return;
      }

      this.logout();
    });
  }

  get auth0Enabled(): boolean {
    return this.auth0EnabledBehaviorSubject$.getValue();
  }

  initializeAuth0(): Observable<string | null> {
    // Log entry point with current URL state for debugging SSO issues
    this.logger.debug('initializeAuth0 starting', {
      pathname: window.location.pathname,
      search: window.location.search,
      hasSsoInFlight: sessionStorage.getItem('sso_in_flight') === '1'
    });

    return combineLatest([
      this.auth0.isLoading$,
      this.auth0.isAuthenticated$,
    ]).pipe(
      filter(([isLoading]) => !isLoading),
      map(([, isAuthenticated]) => isAuthenticated),
      tap((isAuthenticated) => {
        // Log Auth0 SDK state after loading completes
        this.logger.debug('Auth0 SDK ready', {
          isAuthenticated,
          search: window.location.search
        });
      }),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          const isSsoInFlight = sessionStorage.getItem('sso_in_flight') === '1';
          const hasSSOParams =
            window.location.search.includes('sso=') ||
            window.location.search.includes('error=login_required');

          if (isSsoInFlight && hasSSOParams) {
            this.logger.debug('SSO flow in progress, waiting for Auth0 callback to complete', {
              isSsoInFlight,
              hasSSOParams,
              search: window.location.search
            });
            return of(null as string | null);
          }
          // not logged in yet → kick off redirect, emit null
          this.logger.info('User not authenticated, redirecting to Auth0 login', {
            isSsoInFlight,
            hasSSOParams,
            search: window.location.search
          });
          const loginOptions = this.buildLoginOptions();

          return from(
            this.auth0.loginWithRedirect(loginOptions)
          ).pipe(map(() => null as string | null));
        }
        // user is authenticated → get token but don't trigger refresh
        this.logger.debug('User authenticated, fetching access token silently');
        // Use cacheMode: 'on' to avoid triggering refresh token flow
        return from(
          firstValueFrom(
            this.auth0.getAccessTokenSilently({
              cacheMode: 'on',
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace,
              },
            })
          )
        );
      }),
      switchMap(async(token) => {
        if (!token) {
          this.logger.debug('No token received - user not authenticated or redirect in progress');
        }
        if (token) {
          this.logger.debug('Access token received, processing authentication');
          this.auth0TokenBehaviorSubject$.next(token);
          const decoded: ITokenInfo = jwt_decode(token);
          this.segmentService.identifyUser(decoded.meta.authsvc_account_id);
          this.amplitudeFeatureFlagService.setUserId(decoded.meta.authsvc_account_id);
          this.featureFlagService.isEnabled$(FeatureFlagKey.USE_AUTH0_FIX).pipe(take(1)).subscribe((useAuth0Fix) => {
            this.useAuth0Fix = useAuth0Fix;
            if (this.useAuth0Fix) {
              this.logger.addAction('Auth Refresh Info', { message: 'Using Auth0 fix' });
            } else {
              this.logger.addAction('Auth Refresh Info', { message: 'Not using Auth0 fix' });
            }
          });

          try {
            // Check for appState redirect first (e.g., from /therapy-journey)
            // appState$ may not emit if there's no appState, so we use timeout and defaultIfEmpty
            const appState = await firstValueFrom<AppState | null>(
              this.auth0.appState$.pipe(
                timeout(100), // Short timeout to avoid waiting if no appState
                filter((state) => state !== null && state !== undefined),
                take(1),
                defaultIfEmpty(null)
              )
            ).catch((): AppState | null => null);

            if (appState?.target) {
              this.logger.info(`Redirecting to appState target: ${appState.target}`);
              window.location.href = appState.target;
              return null;
            }

            const hasProcessedClaims =
              sessionStorage.getItem('hs_redirected_to');

            if (!hasProcessedClaims) {
              this.logger.debug('Checking for Headspace redirect intent in ID token claims');
              const idToken = await firstValueFrom(
                this.auth0.idTokenClaims$.pipe(
                  filter((claims) => claims !== null && claims !== undefined),
                  take(1)
                )
              );

              // This should be just a path something like /dashboard
              const headspaceRedirectIntent =
                idToken?.[HEADSPACE_CONTEXT_CLAIM];

              if (
                headspaceRedirectIntent &&
                this.isValidRedirectPath(headspaceRedirectIntent)
              ) {
                this.logger.info('Headspace redirect intent found, redirecting', {
                  redirectPath: headspaceRedirectIntent
                });
                sessionStorage.setItem(
                  'hs_redirected_to',
                  headspaceRedirectIntent
                );
                window.location.href = headspaceRedirectIntent;
                return null;
              }
              if (headspaceRedirectIntent && !this.isValidRedirectPath(headspaceRedirectIntent)) {
                this.logger.warn('Headspace redirect intent found but path is invalid', {
                  redirectPath: headspaceRedirectIntent
                });
              }
              if (!headspaceRedirectIntent) {
                this.logger.debug('No Headspace redirect intent found in ID token claims');
              }
            }
          } catch (error) {
            this.logger.error(`Failed to get ID token claims: ${error}`);
            this.logger.addAction('Auth Refresh Failed', { error: `Failed to get ID token claims: ${error}` });
          }
        }
        this.auth0EnabledBehaviorSubject$.next(true);
        this.logger.info('Auth0 initialization complete', {
          hasToken: !!token,
          auth0Enabled: true
        });
        return token;
      })
    );
  }

  setAuth0Success(): void {
    this.authSuccess$.next(true);
  }

  getAuth0Success(): boolean {
    return this.authSuccess$.getValue();
  }

  /**
   * Gets an access token using a barrier pattern to prevent concurrent refresh token exchanges.
   * If a refresh is already in progress, all callers will share the same request.
   * This prevents the "Token could not be decoded or is missing in DB" error caused by
   * multiple processes trying to use the same refresh token simultaneously.
   *
   * @param forceRefresh - If true, bypasses the cache and forces a new token fetch.
   *                       Use sparingly as this always triggers a refresh token exchange.
   * @returns Observable that emits the access token
   */
  getTokenWithBarrier(forceRefresh: boolean = false): Observable<string> {
    // If we have a valid cached token and don't need to force refresh, return it
    if (!forceRefresh) {
      const currentToken = this.auth0TokenBehaviorSubject$.getValue();
      if (currentToken && !this.isTokenExpiringSoon(currentToken)) {
        return of(currentToken);
      }
    }

    // If a refresh is already in progress, return the shared observable
    if (this.refreshInProgress$) {
      this.logger.debug('Token refresh already in progress, sharing existing request');
      this.logger.addAction('Auth Refresh Info', { message: 'Token refresh already in progress, sharing existing request' });
      return this.refreshInProgress$;
    }

    this.logger.info('Starting token refresh with barrier');
    this.logger.addAction('Auth Refresh Info', { message: 'Starting token refresh with barrier' });
    // Create a new shared refresh observable
    this.refreshInProgress$ = from(
      this.auth0.getAccessTokenSilently({
        cacheMode: forceRefresh ? 'off' : 'on',
        authorizationParams: {
          scope: 'offline_access',
          eeNamespace: this.configService.env.eeNamespace,
        },
        // Add timeout to prevent hanging refresh attempts
        timeoutInSeconds: 10,
      })
    ).pipe(
      tap((token) => {
        this.auth0TokenBehaviorSubject$.next(token);
        this.logger.info('Token refresh completed successfully');
        this.logger.addAction('Auth Refresh Success', { message: 'Token refresh completed successfully' });
      }),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.logger.error('Token refresh failed in barrier', { error: errorMessage });
        this.logger.addAction('Auth Refresh Failed', { error: errorMessage });
        if (this.useAuth0Fix) {
        // If refresh token is missing or invalid, try to get from cache first
        // This can happen if the refresh token was consumed but we still have a valid access token
          if (errorMessage.includes('Missing Refresh Token') || errorMessage.includes('invalid refresh token')) {
            this.logger.info('Refresh token missing or invalid, checking for cached access token');
            this.logger.addAction('Auth Refresh Info', { message: 'Refresh token missing or invalid, checking for cached access token' });
            const currentToken = this.auth0TokenBehaviorSubject$.getValue();

            // If we have a token that's still technically valid (even if expiring soon), use it
            if (currentToken && this.isTokenStillValid(currentToken)) {
              this.logger.info('Using existing cached token despite refresh failure');
              this.logger.addAction('Auth Refresh Info', { message: 'Using existing cached token despite refresh failure' });
              return of(currentToken);
            }
          }
        }
        return throwError(() => error);
      }),
      finalize(() => {
        // Clear the in-progress observable so future requests start fresh
        this.refreshInProgress$ = null;
      }),
      // Share the same observable among all concurrent subscribers
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.refreshInProgress$;
  }

  /**
   * Checks if the token is expiring soon (within 2 minutes).
   * This helps proactively refresh tokens before they expire.
   */
  private isTokenExpiringSoon(token: string): boolean {
    try {
      const decoded = jwt_decode<{ exp: number; }>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const twoMinutesInMs = 2 * 60 * 1000;

      return expiresAt - now < twoMinutesInMs;
    } catch {
      // If we can't decode the token, consider it expiring
      return true;
    }
  }

  /**
   * Checks if the token is still technically valid (not yet expired).
   * More lenient than isTokenExpiringSoon - only checks actual expiration.
   */
  private isTokenStillValid(token: string): boolean {
    try {
      const decoded = jwt_decode<{ exp: number; }>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      return expiresAt > now;
    } catch {
      // If we can't decode the token, consider it invalid
      return false;
    }
  }

  logout(
    logoutOptions: LogoutOptions = {
      logoutParams: { returnTo: window.location.origin },
    }
  ): Observable<void> {
    return this.auth0.logout(logoutOptions);
  }

  /**
   * Starts a heartbeat that proactively refreshes tokens before they expire.
   * Uses the barrier pattern to prevent race conditions with other refresh attempts.
   *
   * @param heartbeatInterval - Interval in milliseconds between refresh attempts.
   *                            Default is 14 minutes (840000ms) since access tokens expire after 15 minutes.
   */
  startHeartbeat(heartbeatInterval: number = 14 * 60 * 1000): void {
    if (!this.auth0Enabled) return;

    this.stopHeartbeat();

    this.heartbeatSubscription = interval(heartbeatInterval)
      .pipe(
        switchMap(() => {
          // Check if token is actually expiring soon before forcing a refresh
          const currentToken = this.auth0TokenBehaviorSubject$.getValue();
          if (currentToken && !this.isTokenExpiringSoon(currentToken)) {
            this.logger.debug('Heartbeat skipped - token not expiring soon');
            return of(null);
          }
          // Use barrier WITHOUT forceRefresh to let it use cached token if available
          // This reduces unnecessary refresh token exchanges which can cause issues
          return this.getTokenWithBarrier(!this.useAuth0Fix).pipe(
            catchError((err: unknown) => {
              // Don't let heartbeat errors crash the subscription
              const errorMessage = err instanceof Error ? err.message : String(err);
              this.logger.error('Heartbeat token refresh failed:', { error: errorMessage });

              if (this.useAuth0Fix) {
              // Check if we still have a valid token despite the refresh failure
                const fallbackToken = this.auth0TokenBehaviorSubject$.getValue();
                if (fallbackToken && this.isTokenStillValid(fallbackToken)) {
                  this.logger.info('Heartbeat refresh failed but existing token still valid, continuing');
                  return of(fallbackToken);
                }
              }
              return of(null);
            })
          );
        })
      )
      .subscribe({
        next: (token) => {
          if (token) {
            this.logger.info('Token refreshed via heartbeat');
          }
        },
        error: (err: unknown) => {
          const errorMessage = err instanceof Error ? err.message : String(err);
          this.logger.error('Heartbeat subscription error:', { error: errorMessage });
        },
      });
  }

  stopHeartbeat(): void {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
    }
  }

  setFirstLoginFromAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(true);
  }

  completeFirstTimeAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(false);
  }

  private buildLoginOptions(): { authorizationParams: { scope: string; eeNamespace: string; }; appState?: AppState; } {
    // Check if user is on a path that should be preserved in appState
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const shouldPreservePath = DEEPLINK_REDIRECT_PATHS.some((path) => currentPath.includes(path));

    const loginOptions: { authorizationParams: { scope: string; eeNamespace: string; }; appState?: AppState; } = {
      authorizationParams: {
        scope: 'offline_access',
        eeNamespace: this.configService.env.eeNamespace,
      },
    };

    if (shouldPreservePath) {
      const targetUrl = currentSearch
        ? `${currentPath}${currentSearch}`
        : currentPath;
      loginOptions.appState = { target: targetUrl };
    }

    return loginOptions;
  }

  private isValidRedirectPath(path: string): boolean {
    if (!path.startsWith('/')) {
      return false;
    }
    const allowedPaths = ['/dashboard', '/profile', '/appointments'];

    return allowedPaths.some((allowedPath) => path.startsWith(allowedPath));
  }
}
